// Import react libs
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Import style
import { SearchInput, SearchButton, SearchIcon } from './style';

// Import assets
import { ReactComponent as searchIcon } from '../../assets/icons/search.svg';

// Import texts
import headerTexts from '../../texts/header';
import errorTexts from '../../texts/error';

function SearchBar({
    ICON_WIDTH = 30,
    searchInputWidth,
    setIsSearchInputOpen,
}) {
    // Keep the page location in a variable
    const location = useLocation();
    const navigate = useNavigate();

    // Create a use state to keep the state, the text and the width of the Search input
    const [isInputOpen, setIsInputOpen] = useState(false);
    const [inputText, setInputText] = useState('');
    const [errorTextEmpty, setErrorTextEmpty] = useState(false);

    // Keep a ref of the Search Input and Button
    const searchInputRef = useRef(null);
    const searchButtonRef = useRef(null);

    // Reset the Search Input text when the page change
    useEffect(() => {
        setErrorTextEmpty(false);
        setInputText('');
    }, [location.key]);

    // Put the focus when the input is opened
    useEffect(() => {
        if (isInputOpen && searchInputRef.current) {
            setErrorTextEmpty(false);
            searchInputRef.current.focus();
        }
    }, [isInputOpen]);

    // Put a click listener to close the Search input
    useEffect(() => {
        function handleClickOutsideSearchInput(e) {
            // If the Search bar is open and we click wether outside the Search Input
            // or the Search button then close the Search input
            if (
                searchInputRef.current &&
                !searchInputRef.current.contains(e.target) &&
                !searchButtonRef.current.contains(e.target)
            ) {
                setErrorTextEmpty(false);
                setIsInputOpen(false);
                setIsSearchInputOpen(false);
            }
        }

        // Connect a mousedown listener to the handler
        document.addEventListener('mousedown', handleClickOutsideSearchInput);

        return () =>
            document.removeEventListener(
                'mousedown',
                handleClickOutsideSearchInput,
            );
    });

    // Create a function to handle search button click
    function handleSeachButtonClick() {
        // If the input is closed
        if (!isInputOpen) {
            setIsInputOpen(true);
            setIsSearchInputOpen(true);
            // If the input text is not setted
        } else if (
            !inputText ||
            !inputText.length > 0 ||
            inputText.trim() === ''
        ) {
            setInputText('');
            setErrorTextEmpty(true);
            // If the input is opened and the text is setted
        } else {
            navigate(`/?query=${inputText}`);
            setInputText('');
            setIsInputOpen(false);
            setIsSearchInputOpen(false);
        }
    }

    // Create the component
    return (
        <>
            {isInputOpen && (
                <SearchInput
                    key={errorTextEmpty}
                    ref={searchInputRef}
                    type="text"
                    name="query"
                    value={inputText}
                    placeholder={
                        errorTextEmpty
                            ? errorTexts.textEmptyError
                            : headerTexts.searchBarPlaceholder
                    }
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            setTimeout(() => {
                                handleSeachButtonClick();
                            }, 1);
                        }
                    }}
                    error={errorTextEmpty}
                    width={searchInputWidth}
                />
            )}
            <SearchButton
                ref={searchButtonRef}
                onClick={() => {
                    handleSeachButtonClick();
                }}
            >
                <SearchIcon as={searchIcon} width={ICON_WIDTH} />
            </SearchButton>
        </>
    );
}

export default SearchBar;
